export const SHARE_TEMPLATE_DEFAULT = 'default';
export const SHARE_TEMPLATE_PERSONALIZED = 'personalized';

export function normalizeShareTemplate(value) {
  return value === SHARE_TEMPLATE_PERSONALIZED
    ? SHARE_TEMPLATE_PERSONALIZED
    : SHARE_TEMPLATE_DEFAULT;
}

/**
 * Qué mandar en el share. Sin identidades, solo Terrio (`default`).
 * `personalized` + 0 marcas → 400 del API.
 *
 * @param {string} shareTemplate
 * @param {string|null|undefined} brandId
 * @param {object[]} [brands]
 * @returns {{ shareTemplate: string, brandId: string|null }}
 */
export function resolveShareAppearance(shareTemplate, brandId, brands = []) {
  const hasBrands = Array.isArray(brands) && brands.length > 0;
  if (!hasBrands) {
    return { shareTemplate: SHARE_TEMPLATE_DEFAULT, brandId: null };
  }
  const template = normalizeShareTemplate(shareTemplate);
  if (template !== SHARE_TEMPLATE_PERSONALIZED) {
    return { shareTemplate: SHARE_TEMPLATE_DEFAULT, brandId: null };
  }
  return {
    shareTemplate: SHARE_TEMPLATE_PERSONALIZED,
    brandId: normalizeShareBrandId(brandId),
  };
}

/**
 * Mensaje del API de share (400 de marca, etc.). Si no hay texto, el fallback.
 *
 * @param {object|null|undefined} response
 * @param {string} fallback
 */
export function readShareApiError(response, fallback) {
  const message = String(response?.message || response?.error || '').trim();
  return message || fallback;
}

/**
 * `brand_id` guardado en un share. Vacío se normaliza a `null`, que **no** es
 * "sin marca": significa "seguí la principal de la empresa", y el enlace la
 * sigue si más adelante se marca otra.
 *
 * @param {string|null|undefined} value
 * @returns {string|null}
 */
export function normalizeShareBrandId(value) {
  const trimmed = String(value ?? '').trim();
  return trimmed || null;
}

/**
 * Usa vista branded solo si el share pidió personalizado y el dueño tiene marca configurada.
 * @param {{ share_template?: string, owner_branding?: object|null }|null|undefined} shareData
 */
export function shouldUseBrandedShare(shareData) {
  if (!shareData || typeof shareData !== 'object') return false;
  const template = normalizeShareTemplate(
    shareData.share_template ?? shareData.shareTemplate,
  );
  const branding = shareData.owner_branding ?? shareData.ownerBranding;
  return template === SHARE_TEMPLATE_PERSONALIZED && Boolean(branding?.configured);
}

export function getOwnerBranding(shareData) {
  if (!shareData || typeof shareData !== 'object') return null;
  return shareData.owner_branding ?? shareData.ownerBranding ?? null;
}

/** Hex usable en CSS; fallback si falta o es inválido. */
export function resolveBrandColor(value, fallback) {
  const raw = String(value || '').trim();
  if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(raw)) return fallback;
  if (raw.length === 4) {
    const [, r, g, b] = raw;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return raw;
}

/** Base neutra de las vistas compartidas: el color del usuario nunca la reemplaza. */
export const SHARED_NEUTRAL_SURFACE = '#141414';

/** Header y footer de vistas compartidas (default y branded). */
export const SHARED_CHROME_SURFACE = '#1C1C1C';

/** Chip del header en vistas compartidas (default y branded). Copy: `Lista {nombre}`. */
export const SHARED_VIEW_BADGE_CHIP_SX = {
  color: '#E8F4F4',
  border: '1px solid #383838',
  bgcolor: SHARED_CHROME_SURFACE,
  height: 30,
  borderRadius: '4px',
};

function hexToRgb(hex) {
  const normalized = resolveBrandColor(hex, null);
  if (!normalized) return null;
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
}

function channelToLinear(value) {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** Luminancia relativa WCAG (0 = negro, 1 = blanco). */
export function getRelativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  return (
    0.2126 * channelToLinear(rgb.r)
    + 0.7152 * channelToLinear(rgb.g)
    + 0.0722 * channelToLinear(rgb.b)
  );
}

export function getContrastRatio(hexA, hexB) {
  const a = getRelativeLuminance(hexA);
  const b = getRelativeLuminance(hexB);
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Texto legible sobre un fondo arbitrario (el color de marca puede ser claro u oscuro).
 * @returns {'#FFFFFF'|'#111111'}
 */
export function getContrastTextColor(backgroundHex) {
  const white = getContrastRatio(backgroundHex, '#FFFFFF');
  const black = getContrastRatio(backgroundHex, '#111111');
  return white >= black ? '#FFFFFF' : '#111111';
}

function mixWithWhite(hex, amount) {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const mix = (channel) => Math.round(channel + (255 - channel) * amount);
  const toHex = (channel) => mix(channel).toString(16).padStart(2, '0');
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

/**
 * Aclara acentos muy oscuros para que la línea/ícono siga visible sobre la base neutra.
 * No cambia colores que ya contrastan.
 */
export function ensureAccentOnDark(hex, surface = SHARED_NEUTRAL_SURFACE) {
  const resolved = resolveBrandColor(hex, null);
  if (!resolved) return hex;

  let candidate = resolved;
  for (let step = 0; step < 6; step += 1) {
    if (getContrastRatio(candidate, surface) >= 2.6) return candidate;
    candidate = mixWithWhite(resolved, 0.15 * (step + 1));
  }
  return candidate;
}

/** Hex + alpha en formato #RRGGBBAA (para bordes/halos sutiles). */
export function withAlpha(hex, alpha) {
  const resolved = resolveBrandColor(hex, null);
  if (!resolved) return 'transparent';
  const clamped = Math.max(0, Math.min(1, alpha));
  const alphaHex = Math.round(clamped * 255).toString(16).padStart(2, '0');
  return `${resolved}${alphaHex}`;
}

/**
 * Paleta de marca para vistas compartidas branded.
 * Por ahora solo se aplica el color primario (el secundario se configura
 * en el perfil pero no se usa en la vista del visitante hasta definir UX).
 *
 * @param {object|null|undefined} branding
 * @param {string} [surface]
 */
export function resolveShareBrandPalette(branding, surface = SHARED_NEUTRAL_SURFACE) {
  const primaryRaw = resolveBrandColor(branding?.primary_color, '#489291');
  const primary = ensureAccentOnDark(primaryRaw, surface);

  return {
    primary,
    contrastOnPrimary: getContrastTextColor(primary),
    primarySoft: withAlpha(primary, 0.18),
    primaryBorder: withAlpha(primary, 0.5),
  };
}

export function buildWhatsAppHref(whatsapp) {
  const digits = String(whatsapp || '').replace(/\D/g, '');
  if (!digits) return null;
  return `https://wa.me/${digits}`;
}

export function buildTelHref(phone) {
  const trimmed = String(phone || '').trim();
  if (!trimmed) return null;
  return `tel:${trimmed.replace(/\s+/g, '')}`;
}

export function buildMailtoHref(email) {
  const trimmed = String(email || '').trim();
  if (!trimmed) return null;
  return `mailto:${trimmed}`;
}
